import React, { useEffect, useState } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';

declare global {
    interface Window {
        google: any;
        googleTranslateElementInit: () => void;
    }
}

const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'sw', name: 'Kiswahili', flag: '🇹🇿' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'ar', name: 'العربية', flag: '🇦🇪', dir: 'rtl' },
];

const LanguageSelector: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentLang, setCurrentLang] = useState('en');

    useEffect(() => {
        // Check for existing cookie
        const getCookie = (name: string) => {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2) return parts.pop()?.split(';').shift();
        };

        const googtrans = getCookie('googtrans');
        if (googtrans) {
            const lang = googtrans.split('/').pop();
            if (lang) setCurrentLang(lang);
        }
    }, []);

    const changeLanguage = (langCode: string) => {
        setCurrentLang(langCode);
        setIsOpen(false);

        // Set google translate cookie
        document.cookie = `googtrans=/en/${langCode}; path=/; domain=${window.location.hostname}`;
        document.cookie = `googtrans=/en/${langCode}; path=/`;

        // Trigger google translate
        const element = document.querySelector('.goog-te-combo') as HTMLSelectElement;
        if (element) {
            element.value = langCode;
            element.dispatchEvent(new Event('change'));
        } else {
            // Fallback: reload if element not found yet (rare) or just reload to apply everything cleanly
            window.location.reload();
        }
    };

    const currentLanguage = languages.find(l => l.code === currentLang) || languages[0];

    return (
        <div className="relative z-50">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-2 rounded-full text-gray-300 hover:text-white hover:bg-white/10 transition-all"
                title="Change Language"
            >
                <Globe size={22} />
            </button>

            {isOpen && (
                <>
                    <div
                        className="fixed inset-0 z-40 bg-transparent"
                        onClick={() => setIsOpen(false)}
                    ></div>
                    <div className="absolute right-0 mt-2 w-48 bg-neutral-900 border border-gray-800 rounded-sm shadow-xl z-50 animate-in fade-in zoom-in-95 duration-200">
                        <div className="py-1">
                            {languages.map((lang) => (
                                <button
                                    key={lang.code}
                                    onClick={() => changeLanguage(lang.code)}
                                    className={`w-full text-left px-4 py-3 text-sm font-medium hover:bg-white/5 transition-colors flex items-center justify-between group ${currentLang === lang.code ? 'text-automax-red' : 'text-gray-300'
                                        }`}
                                >
                                    <span className="flex items-center gap-3">
                                        <span className="text-lg">{lang.flag}</span>
                                        {lang.name}
                                    </span>
                                    {currentLang === lang.code && <Check size={14} />}
                                </button>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default LanguageSelector;
